/**
 * Month/year value formatting and validation
 *
 * Provides formatting and validation functions for credit card expiration date input.
 * Format: MM / YYYY (e.g., "01 / 2030")
 *
 * @example
 * ```typescript
 * import { formatInput, parseValue, isValidMonth, isValidYear, isExpired } from './monthYearValue';
 *
 * // Format user input
 * formatInput('012030'); // Returns "01 / 2030"
 *
 * // Parse formatted value
 * parseValue('01 / 2030'); // Returns { month: '01', year: '2030' }
 *
 * // Validate
 * isValidMonth('12'); // true
 * isValidYear('2030'); // true
 * isExpired('01', '2020'); // true (expired)
 * ```
 */

import { endOfMonth, getYear, isPast, parse } from 'date-fns';
import { extractDigits, isDigit, isString } from '@cvo/domain/utils';
import { MONTH_YEAR_CONFIG, type MonthYearValue, type YearFormat } from './MonthYearInput.model';

const {
  separator,
  monthLength,
  minMonth,
  maxMonth,
  minYear,
  maxYear,
} = MONTH_YEAR_CONFIG;

const EXPECTED_PARTS = 2;

/**
 * Normalizes a short year (YY) to full year (YYYY)
 */
export function normalizeYear(year: string, format: YearFormat = 'YYYY'): string {
  if (format === 'YY') {
    const yearPrefix = getYear(new Date()).toString()
      .slice(0, format.length);

    return `${yearPrefix}${year}`;
  }

  return year;
}

/**
 * Validates if the month string is valid (01-12)
 */
export function isValidMonth(month: string): boolean {
  if (!month || month.length !== monthLength || !isDigit(month)) {
    return false;
  }

  const monthNum = parseInt(month, 10);
  return monthNum >= minMonth && monthNum <= maxMonth;
}

/**
 * Validates if the year string is valid (2 or 4 digits, reasonable range)
 * @param year - Year string to validate
 * @param format - Year format: 'YY' or 'YYYY' (default: 'YYYY')
 */
export function isValidYear(year: string, format: YearFormat = 'YYYY'): boolean {
  if (year?.length !== format.length || !isDigit(year)) {
    return false;
  }

  const normalizedYear = normalizeYear(year, format);
  const yearNum = parseInt(normalizedYear, 10);

  return yearNum >= minYear && yearNum <= maxYear;
}

/**
 * Creates a Date object representing the end of the given month/year
 * Used for expiration comparison (card valid through end of month)
 * @param month - Month string (MM)
 * @param year - Year string (YY or YYYY)
 * @param format - Year format: 'YY' or 'YYYY' (default: 'YYYY')
 */
function createExpirationDate(month: string, year: string, format: YearFormat = 'YYYY'): Date {
  const normalizedYear = normalizeYear(year, format);
  const date = parse(`${normalizedYear}-${month}`, 'yyyy-MM', new Date());

  return endOfMonth(date);
}

/**
 * Checks if the given month/year combination has expired
 * @param month - Month string (MM)
 * @param year - Year string (YY or YYYY)
 * @param format - Year format: 'YY' or 'YYYY' (default: 'YYYY')
 */
export function isExpired(month: string, year: string, format: YearFormat = 'YYYY'): boolean {
  if (!isValidMonth(month) || !isValidYear(year, format)) {
    return true;
  }

  return isPast(createExpirationDate(month, year, format));
}

/**
 * Formats raw input into MM / YYYY or MM / YY format
 * Auto-inserts " / " separator after 2 digits
 * @param input - Raw input string
 * @param format - Year format: 'YY' or 'YYYY' (default: 'YYYY')
 */
export function formatInput(input: string, format: YearFormat = 'YYYY'): string {
  const digits = extractDigits(input);

  // Guard: if no digits extracted, return previous value (reject non-numeric input)
  if (digits.length === 0) {
    return '';
  }

  const limitedDigits = digits.slice(0, monthLength + format.length);

  return buildFormattedValue(limitedDigits);
}

/**
 * Builds the formatted month/year string from digits
 */
function buildFormattedValue(digits: string): string {
  const month = digits.slice(0, monthLength);
  const year = digits.slice(monthLength);

  if (digits.length < monthLength) {
    return month;
  }

  return year.length > 0
    ? `${month}${separator}${year}`
    : `${month}${separator}`;
}

/**
 * Extracts month and year parts from formatted string
 */
function extractParts(formatted: string): { month: string; year: string } | undefined {
  const parts = formatted.split(separator.trim());

  if (parts.length !== EXPECTED_PARTS) {
    return undefined;
  }

  return {
    month: parts[0]?.trim() ?? '',
    year: parts[1]?.trim() ?? '',
  };
}

/**
 * Validates the format of parsed month and year parts
 */
function hasValidParts(month: string, year: string, format: YearFormat = 'YYYY'): boolean {
  const hasValidLengths = month.length === monthLength && year.length === format.length;
  return hasValidLengths && isDigit(month) && isDigit(year);
}

/**
 * Parses a formatted string into month and year components
 * @param formatted - Formatted string (e.g., "01 / 2030" or "01 / 30")
 * @param format - Year format: 'YY' or 'YYYY' (default: 'YYYY')
 */
export function parseValue(formatted: string, format: YearFormat = 'YYYY'): MonthYearValue | undefined {
  if (!isString(formatted) || formatted.length === 0) {
    return undefined;
  }

  const parts = extractParts(formatted);

  if (!parts) {
    return undefined;
  }

  return hasValidParts(parts.month, parts.year, format) ? parts : undefined;
}
